import type { ReactNode } from "react";
import { Button, Card, Empty, Flex, Space, Spin, Tag, Typography } from "antd";
import type { DataStatus } from "@deltabeacon/contracts";

export function PageHeader({
  title,
  action,
}: {
  title: string;
  action?: ReactNode;
}) {
  return (
    <Flex
      className="page-heading"
      justify="space-between"
      align="flex-end"
      gap={20}
      wrap
    >
      <Typography.Title level={2}>{title}</Typography.Title>
      {action}
    </Flex>
  );
}

const statusMap: Record<DataStatus, { label: string; color: string }> = {
  LIVE: { label: "实时", color: "success" },
  SIMULATED: { label: "模拟数据", color: "processing" },
  DELAYED: { label: "数据延迟", color: "warning" },
  UNAVAILABLE: { label: "暂无数据", color: "default" },
};

export function DataStatusTag({ status }: { status: DataStatus }) {
  const item = statusMap[status];
  return (
    <Tag color={item.color} className="status-tag">
      <span className={`status-dot status-${status.toLowerCase()}`} />
      {item.label}
    </Tag>
  );
}

export function Percent({ value }: { value: number }) {
  return (
    <Typography.Text
      className={`mono change ${value >= 0 ? "up" : "down"}`}
      strong
    >
      {value >= 0 ? "+" : ""}
      {value.toFixed(2)}%
    </Typography.Text>
  );
}

export function MetricCard({
  label,
  value,
  tone = "blue",
}: {
  label: string;
  value: ReactNode;
  tone?: "blue" | "cyan" | "up" | "down";
}) {
  return (
    <Card className={`metric-card metric-${tone}`}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <div className="metric-value">{value}</div>
    </Card>
  );
}

export function QueryState({
  loading,
  error,
  empty,
  onRetry,
  children,
}: {
  loading: boolean;
  error?: Error | null;
  empty?: boolean;
  onRetry?: () => void;
  children: ReactNode;
}) {
  if (loading)
    return (
      <Flex className="query-state" align="center" justify="center">
        <Spin size="large" />
      </Flex>
    );
  if (error)
    return (
      <Card className="query-state">
        <Empty description={error.message} image={Empty.PRESENTED_IMAGE_SIMPLE}>
          {onRetry ? <Button onClick={onRetry}>重新加载</Button> : null}
        </Empty>
      </Card>
    );
  if (empty)
    return (
      <Card className="query-state">
        <Empty description="暂无记录" />
      </Card>
    );
  return <>{children}</>;
}

export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <Space className="filter-bar" size={12} wrap>
      {children}
    </Space>
  );
}
